import { Component, input, output, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Column, SetupWizardDynamicTable } from '../../../setup-wizard-dynamic-table/setup-wizard-dynamic-table';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { CommonModule } from '@angular/common';
import { BrowserService } from '../../../../../../../services/browser.service';
import { HeaderColumn } from '../../../setup-wizard-dynamic-table/header-column';
import { RowColumn } from '../../../setup-wizard-dynamic-table/row-column';
import {
	BeforeAfter,
	ACTIVITY_CSV_LABELS,
	SetupWizardBooleanType,
	SetupWizardActivity,
	ActivityCsvKeys,
	ActivityCsvTemplate,
	EMPTY_ACTIVITY_CSV_TEMPLATE,
	SetupWizardPayload,
} from '@mira-avant/mtlibs';
import { SetupWizardBeforeAfter } from '../../setup-wizard-before-after/setup-wizard-before-after';
import {
	makeStatusColumn,
	SetupWizardBeforeAfterStatus,
} from '../../setup-wizard-before-after-status/setup-wizard-before-after-status';
import { SetupWizard } from '../../../../../../../services/setup-wizard';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { MAX_FILTER_OPTIONS_ALLOWED } from '../../setup-wizard-edit-steps';

const FULL_WIDTH_BREAKPOINT = 1510;

@Component({
	selector: 'app-setup-wizard-activities-table',
	imports: [
		CommonModule,
		SetupWizardDynamicTable,
		NzIconModule,
		RowColumn,
		HeaderColumn,
		SetupWizardBeforeAfter,
		NzTooltipModule,
		NzSpinModule,
		NzButtonModule,
		NzPopconfirmModule,
		SetupWizardBeforeAfterStatus,
	],
	standalone: true,
	templateUrl: './setup-wizard-activities-table.html',
	styleUrl: './setup-wizard-activities-table.scss',
})
export class SetupWizardActivitiesTable {
	private clientUuid: string | undefined;
	private sub?: Subscription;
	private resizeSub?: Subscription;
	public loading = signal<boolean>(true);
	public pageSize = input<number>(10);
	public rows = signal<SetupWizardActivity[]>([]);
	readonly rowType = null! as BeforeAfter<ActivityCsvTemplate>; // type inference only
	public columns = signal<Column[]>([]);
	private wideColumns: Column[] = [];
	private narrowColumns: Column[] = [];
	public onEdit = output<SetupWizardActivity>();
	public onToggleDisable = output<SetupWizardActivity>();
	public onRemove = output<SetupWizardActivity>();
	public onReset = output<SetupWizardActivity>();
	public enableEditButton = input<boolean>(true);
	public enableToggleDisableButton = input<boolean>(true);
	public enableRemoveButton = input<boolean>(true);
	public enableResetButton = input<boolean>(true);
	public selectedRow = input<BeforeAfter<ActivityCsvTemplate> | null>(null);
	public ActivityCsvKeys = ActivityCsvKeys;
	public ACTIVITY_CSV_LABELS = ACTIVITY_CSV_LABELS;
	public SetupWizardBooleanType = SetupWizardBooleanType;

	private ssoActivityIdFilterOptions: { text: string; value: string }[] = [];
	private displayOrderFilterOptions: { text: string; value: number }[] = [];
	private enNameFilterOptions: { text: string; value: string }[] = [];

	private ssoActivityIdColumn: Column | undefined;
	private defaultLangIdColumn: Column | undefined;
	private displayOrderColumn: Column | undefined;
	private isInstructionColumn: Column | undefined;
	private enNameColumn: Column | undefined;
	private activeColumn: Column | undefined;

	constructor(
		private route: ActivatedRoute,
		private browserService: BrowserService,
		private setupWizard: SetupWizard
	) {
		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	async ngOnInit() {
		await this.initTable();

		this.sub?.add(
			this.setupWizard.preview$.subscribe(payload => {
				this.loading.set(true);

				if (payload) {
					this.updateRows(payload);
				}

				this.loading.set(false);
			})
		);
	}

	private updateRows(payload: SetupWizardPayload) {
		this.rows.set(payload.payload.activities);
		this.updateFilterOptions();
	}

	private updateFilterOptions(): void {
		const rows = this.rows();

		this.ssoActivityIdFilterOptions = [...new Set(rows.map(row => row.before?.sso_activity_id || ''))]
			.sort()
			.map(value => ({
				text: value,
				value: value,
			}));

		this.displayOrderFilterOptions = [...new Set(rows.map(row => row.before?.display_order || 0))]
			.sort()
			.map(value => ({
				text: value.toString(),
				value: value,
			}));

		this.enNameFilterOptions = [...new Set(rows.map(row => row.before?.en_name || ''))].sort().map(value => ({
			text: value,
			value: value,
		}));

		if (this.ssoActivityIdFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.ssoActivityIdColumn!.filterFn = undefined;
			this.ssoActivityIdColumn!.filterOptions = undefined;
		} else {
			this.ssoActivityIdColumn!.filterOptions = this.ssoActivityIdFilterOptions;
			this.ssoActivityIdColumn!.filterFn = (list: string[], item: SetupWizardActivity) =>
				list.some(name => item.before?.sso_activity_id?.indexOf(name) !== -1);
		}

		if (this.displayOrderFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.displayOrderColumn!.filterFn = undefined;
			this.displayOrderColumn!.filterOptions = undefined;
		} else {
			this.displayOrderColumn!.filterOptions = this.displayOrderFilterOptions;
			this.displayOrderColumn!.filterFn = (list: number[], item: SetupWizardActivity) =>
				list.some(name => item.before?.display_order === name);
		}

		if (this.enNameFilterOptions.length > MAX_FILTER_OPTIONS_ALLOWED) {
			this.enNameColumn!.filterFn = undefined;
			this.enNameColumn!.filterOptions = undefined;
		} else {
			this.enNameColumn!.filterOptions = this.enNameFilterOptions;
			this.enNameColumn!.filterFn = (list: string[], item: SetupWizardActivity) =>
				list.some(name => item.before?.en_name?.indexOf(name) !== -1);
		}
	}

	public async initTable(): Promise<void> {
		try {
			this.initColumns();

			this.resizeSub?.unsubscribe();
			this.resizeSub = this.browserService.onResize$.subscribe((width: number) => {
				if (width >= FULL_WIDTH_BREAKPOINT) {
					this.columns.set(this.wideColumns);
				} else {
					this.columns.set(this.narrowColumns);
				}
			});
		} catch (error) {
			console.error('Error loading data:', error);
		}
	}

	private initColumns(): void {
		Object.keys(EMPTY_ACTIVITY_CSV_TEMPLATE).map(key => {
			switch (key) {
				case ActivityCsvKeys.ssoActivityId:
					this.ssoActivityIdColumn = {
						key,
						name: ACTIVITY_CSV_LABELS[ActivityCsvKeys.ssoActivityId],
						sortOrder: null,
						sortFn: (a: SetupWizardActivity, b: SetupWizardActivity) =>
							a.before?.sso_activity_id!.localeCompare(b.before?.sso_activity_id!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case ActivityCsvKeys.defaultLangId:
					this.defaultLangIdColumn = {
						key,
						name: ACTIVITY_CSV_LABELS[ActivityCsvKeys.defaultLangId],
						sortOrder: null,
						sortFn: (a: SetupWizardActivity, b: SetupWizardActivity) =>
							a.before?.default_lang_id!.localeCompare(b.before?.default_lang_id!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case ActivityCsvKeys.displayOrder:
					this.displayOrderColumn = {
						key,
						name: ACTIVITY_CSV_LABELS[ActivityCsvKeys.displayOrder],
						sortOrder: null,
						sortFn: (a: SetupWizardActivity, b: SetupWizardActivity) =>
							(a.before?.display_order ?? -1) - (b.before?.display_order ?? -1),
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case ActivityCsvKeys.isInstruction:
					this.isInstructionColumn = {
						key,
						name: ACTIVITY_CSV_LABELS[ActivityCsvKeys.isInstruction],
						sortOrder: null,
						sortFn: (a: SetupWizardActivity, b: SetupWizardActivity) =>
							(a.after?.is_instruction ?? a.before?.is_instruction)
								?.toString()
								.localeCompare((b.after?.is_instruction ?? b.before?.is_instruction)?.toString()!) ??
							-1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(
							value => ({
								text: value,
								value: value,
							})
						),
						filterFn: (list: string[], item: SetupWizardActivity) =>
							list.some(
								name =>
									(item.after?.is_instruction ?? item.before?.is_instruction)
										?.toString()
										?.indexOf(name) !== -1
							),
					};
					break;
				case ActivityCsvKeys.enName:
					this.enNameColumn = {
						key,
						name: ACTIVITY_CSV_LABELS[ActivityCsvKeys.enName],
						sortOrder: null,
						sortFn: (a: SetupWizardActivity, b: SetupWizardActivity) =>
							a.before?.en_name!.localeCompare(b.before?.en_name!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
					};
					break;
				case ActivityCsvKeys.active:
					this.activeColumn = {
						key,
						name: ACTIVITY_CSV_LABELS[ActivityCsvKeys.active],
						sortOrder: null,
						// For sorting prefer the after.active property if it exists (is true or false)
						// and fall back to before.active if it is not present
						sortFn: (a: SetupWizardActivity, b: SetupWizardActivity) =>
							(a.after?.active ?? a.before?.active)
								?.toString()
								.localeCompare((b.after?.active ?? b.before?.active)?.toString()!) ?? -1,
						sortDirections: ['ascend', 'descend', null],
						filterMultiple: true,
						filterOptions: [...new Set([SetupWizardBooleanType.TRUE, SetupWizardBooleanType.FALSE])].map(
							value => ({
								text: value,
								value: value,
							})
						),
						filterFn: (list: string[], item: SetupWizardActivity) =>
							list.some(
								name => (item.after?.active ?? item.before?.active)?.toString()?.indexOf(name) !== -1
							),
					};
					break;
				default:
					break;
			}
		});

		if (
			!this.ssoActivityIdColumn ||
			!this.defaultLangIdColumn ||
			!this.displayOrderColumn ||
			!this.isInstructionColumn ||
			!this.enNameColumn ||
			!this.activeColumn
		) {
			throw new Error('One or more columns are not defined');
		}

		const statusColumn: Column = makeStatusColumn<SetupWizardActivity>();

		this.wideColumns = [
			this.ssoActivityIdColumn,
			this.enNameColumn,
			this.displayOrderColumn,
			this.activeColumn,
			this.isInstructionColumn,
			statusColumn,
		];

		this.narrowColumns = [this.ssoActivityIdColumn, this.enNameColumn, this.activeColumn];

		this.columns.set(this.wideColumns);
	}

	onEditClick(row: SetupWizardActivity) {
		this.onEdit.emit(row);
	}

	onToggleDisableClick(row: SetupWizardActivity) {
		this.onToggleDisable.emit(row);
	}

	onRemoveClick(row: SetupWizardActivity) {
		this.onRemove.emit(row);
	}

	onResetClick(row: SetupWizardActivity) {
		this.onReset.emit(row);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
		this.resizeSub?.unsubscribe();
	}
}
